import React from "react";
import { Link } from "react-router-dom";
import { useBranding } from "../../contexts/BrandingContext";
import Shimmer from "../Common/Shimmer";
// The mark goes in the 34px square beside the name; the wordmark only fits the
// footer, where it has a height and its own width.
import mark from "../../Assets/Images/Logos/favicon.png";
import logo from "../../Assets/Images/Logos/lms_logo.png";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); TermsAndConditions.scss deleted with it.
//
// This page is thirteen prose sections with identical typography, which is the case
// §6.3b names: a string applied at thirteen call sites, not thirteen copies of the
// same twelve utilities. The child combinators are deliberate — `>` and not a
// descendant space — so the contact card nested in section 13 keeps its own rules
// instead of inheriting the section's, which is exactly what the old
// `.terms-section p` / `.terms-contact-card p` specificity tie was resolving by
// source order.
const SECTION =
  "tw-mb-9 last:tw-mb-0 " +
  "[&>h2]:tw-mb-3.5 [&>h2]:tw-border-0 [&>h2]:tw-border-b-2 [&>h2]:tw-border-solid " +
  "[&>h2]:tw-border-primary/15 [&>h2]:tw-pb-2.5 [&>h2]:tw-text-xl [&>h2]:tw-font-medium " +
  "[&>h2]:tw-text-content max-md:[&>h2]:tw-text-[1.12rem] " +
  "[&>h3]:tw-mb-2 [&>h3]:tw-mt-[18px] [&>h3]:tw-text-[1.02rem] [&>h3]:tw-font-semibold " +
  "[&>h3]:tw-text-content " +
  "[&>p]:tw-mb-3 [&>p]:tw-text-[0.93rem] [&>p]:tw-leading-[1.7] [&>p]:tw-text-content-muted " +
  "[&>ul]:tw-mb-4 [&>ul]:tw-mt-2 [&>ul]:tw-ps-5 " +
  "[&>ul>li]:tw-mb-1.5 [&>ul>li]:tw-ps-2 [&>ul>li]:tw-text-[0.93rem] " +
  "[&>ul>li]:tw-leading-[1.7] [&>ul>li]:tw-text-content-muted " +
  "[&>ul>li]:marker:tw-text-primary";

// A 160deg wash between the two always-dark surfaces. It does NOT invert with the
// theme and should not: a public legal page is one page, and the hero is its
// masthead in both. The subtitle's rgba(255,255,255,.55) is the same ink as the
// heading at 55% — `on-surface-dark/55` — rather than a literal that only happened
// to be white because the panel was always dark.
const HERO =
  "tw-px-6 tw-pb-14 tw-pt-16 tw-text-center tw-text-on-surface-dark " +
  "tw-bg-[linear-gradient(160deg,hsl(var(--surface-dark))_0%,color-mix(in_srgb,hsl(var(--surface-dark))_50%,hsl(var(--surface-dark-raised)))_50%,hsl(var(--surface-dark-raised))_100%)] " +
  "max-md:tw-px-5 max-md:tw-pb-9 max-md:tw-pt-10";

// Two shadow layers kept, both literals gone. --card-shadow is rgba(17,24,39,.04)
// in light and rgba(0,0,0,.32) in dark; the sheet's rgba(0,0,0,.04) was the light
// value hardcoded, so the card had effectively no lift on a dark background.
const CARD =
  "tw-rounded-lg tw-bg-surface-raised tw-px-14 tw-py-12 " +
  "tw-shadow-[0_1px_3px_var(--card-shadow),0_4px_16px_var(--card-shadow)] " +
  "max-md:tw-rounded max-md:tw-px-5 max-md:tw-py-7 " +
  "max-[480px]:tw-px-4 max-[480px]:tw-py-5";

const CONTACT_CARD =
  "tw-mt-3 tw-rounded-md tw-border-0 tw-border-s-4 tw-border-solid tw-border-primary " +
  "tw-bg-surface tw-px-6 tw-py-5 " +
  "[&>p]:tw-mb-1 [&>p]:tw-text-[0.9rem] [&>p]:tw-leading-[1.7] " +
  "[&>p]:tw-text-content-muted [&>p:last-child]:tw-mb-0";

const HEADER_BTN =
  "tw-inline-flex tw-items-center tw-rounded-sm tw-px-5 tw-py-2 tw-text-[0.88rem] " +
  "tw-font-semibold tw-no-underline tw-transition-all tw-duration-200";

// Hidden below 768px in the original — on a phone the primary action is the only
// one worth the width.
const HEADER_BTN_OUTLINE =
  HEADER_BTN +
  " tw-border-[1.5px] tw-border-solid tw-border-line tw-bg-surface-raised tw-text-content " +
  "hover:tw-border-[color-mix(in_srgb,var(--border-primary)_90%,black)] " +
  "hover:tw-bg-surface hover:tw-text-content max-md:tw-hidden";

const HEADER_BTN_PRIMARY =
  HEADER_BTN +
  " tw-border-[1.5px] tw-border-solid tw-border-primary-solid tw-bg-primary-solid tw-text-primary-solid-fg " +
  "hover:tw-bg-[color-mix(in_srgb,hsl(var(--primary-solid))_88%,black)] hover:tw-text-primary-solid-fg";

const SHELL_INNER = "tw-mx-auto tw-w-full tw-max-w-[1000px]";

const TermsAndConditions = () => {
  const {
    brandName, legalName, logoUrl, faviconUrl, contactEmail, supportEmail, loading,
  } = useBranding();

  // A custom domain or an institute subdomain serves this page as the INSTITUTE's
  // terms, so every name on it is theirs. The branding endpoint always answers a
  // resolved institute with a legal name (it falls back to the brand name), so an
  // empty one means no institute — our own host, where the platform is the party.
  const isPlatformHost = !legalName;
  const name = brandName;
  const company = legalName || brandName;
  const email = contactEmail || (isPlatformHost ? supportEmail || "support@lmscampus.com" : "");

  // Until branding lands, DEFAULTS say "LMS Campus" — which is exactly the name a
  // white-labelled institute's visitor must not see, even for a frame.
  const ready = !loading;

  return (
    <div className="tw-flex tw-min-h-screen tw-flex-col tw-bg-surface tw-font-sans">
      {/* Header */}
      <header className="tw-sticky tw-top-0 tw-z-sticky tw-border-0 tw-border-b tw-border-solid tw-border-line tw-bg-surface-raised">
        <div
          className={`${SHELL_INNER} tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-3.5`}
        >
          <Link
            to="/login"
            className="tw-flex tw-items-center tw-gap-2.5 tw-no-underline"
          >
            {ready && (
              <>
                <img
                  src={faviconUrl || logoUrl || mark}
                  alt=""
                  className="tw-h-[34px] tw-w-[34px] tw-object-contain"
                />
                <span className="tw-text-[1.1rem] tw-font-medium tw-tracking-[-0.01em] tw-text-content max-[480px]:tw-hidden">
                  {name}
                </span>
              </>
            )}
          </Link>
          <div className="tw-flex tw-items-center tw-gap-2.5">
            <Link to="/login" className={HEADER_BTN_OUTLINE}>
              Sign In
            </Link>
            <Link to="/signup" className={HEADER_BTN_PRIMARY}>
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className={HERO}>
        <h1 className="tw-mb-2.5 tw-mt-0 tw-text-[2.2rem] tw-font-extrabold tw-tracking-[-0.02em] max-md:tw-text-[1.6rem]">
          Terms &amp; Conditions
        </h1>
        <p className="tw-m-0 tw-text-[0.95rem] tw-text-on-surface-dark/55">
          Last updated: February 20, 2026
        </p>
      </div>

      {/* Content */}
      <div
        className={`${SHELL_INNER} tw-flex-1 tw-px-6 tw-pb-[60px] tw-pt-10 max-[480px]:tw-px-3 max-[480px]:tw-pb-10 max-[480px]:tw-pt-5`}
      >
        <div className={CARD}>
          {!ready ? (
            <Shimmer height={96} count={4} />
          ) : (
          <>
          <section className={SECTION}>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using the {name} platform ("Service"), you acknowledge that you
              have read, understood, and agree to be bound by these Terms and Conditions ("Terms"). If
              you do not agree with any part of these Terms, you must not use our Service.
            </p>
            <p>
              These Terms apply to all visitors, users, students, instructors, administrators, and
              others who access or use the Service.
            </p>
          </section>

          <section className={SECTION}>
            <h2>2. Description of Service</h2>
            <p>
              {name} is a virtual classroom and learning management platform that provides:
            </p>
            <ul>
              <li>Live interactive online classes and virtual classrooms</li>
              <li>Course management and syllabus tracking</li>
              <li>Assignment creation, submission, and grading</li>
              <li>AI-powered learning tools and practice sessions</li>
              <li>Mock interview preparation and practice</li>
              <li>Student performance tracking and attendance management</li>
              <li>Fee collection and payment processing</li>
              <li>Class recordings and educational content delivery</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2>3. User Accounts</h2>
            <h3>3.1 Registration</h3>
            <p>
              To use certain features of the Service, you must register for an account. You agree to
              provide accurate, current, and complete information during registration and to update
              such information to keep it accurate, current, and complete.
            </p>
            <h3>3.2 Account Security</h3>
            <p>
              You are responsible for safeguarding your password and for all activities that occur
              under your account. You agree to notify us immediately of any unauthorized use of your
              account. We will not be liable for any loss or damage arising from your failure to
              protect your account credentials.
            </p>
            <h3>3.3 Account Types</h3>
            <p>
              The Service supports multiple user roles including Students, Instructors, and
              Administrators. Each role has specific permissions and responsibilities within the
              platform.
            </p>
          </section>

          <section className={SECTION}>
            <h2>4. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Violate any applicable local, state, national, or international law</li>
              <li>
                Transmit any material that is defamatory, offensive, or otherwise objectionable
              </li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>
                Interfere with or disrupt the Service or servers connected to the Service
              </li>
              <li>
                Attempt to gain unauthorized access to other user accounts or computer systems
              </li>
              <li>Upload viruses, malware, or other malicious code</li>
              <li>
                Share account credentials or allow multiple individuals to use a single account
              </li>
              <li>
                Copy, distribute, or disclose any part of the Service in any medium without
                authorization
              </li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2>5. Intellectual Property</h2>
            <h3>5.1 Platform Content</h3>
            <p>
              The Service and its original content (excluding content provided by users), features,
              and functionality are and will remain the exclusive property of {company} and its
              licensors. The Service is protected by copyright, trademark, and other laws.
            </p>
            <h3>5.2 User Content</h3>
            <p>
              Users retain ownership of content they create and upload to the platform, including
              assignments, course materials, and educational resources. By uploading content, you
              grant {company} a non-exclusive license to use, display, and distribute such
              content within the platform for educational purposes.
            </p>
          </section>

          <section className={SECTION}>
            <h2>6. Payments &amp; Fees</h2>
            <p>
              Certain features of the Service may require payment of fees. All fees are stated in
              Indian Rupees (INR) unless otherwise specified. Payment processing is handled securely
              through authorized payment gateways. Refund policies are determined by individual
              institutes and course providers on the platform.
            </p>
          </section>

          <section className={SECTION}>
            <h2>7. Privacy Policy</h2>
            <p>
              Your privacy is important to us. Our collection and use of personal information in
              connection with the Service is described in our Privacy Policy. By using the Service, you
              consent to the collection and use of information as outlined therein.
            </p>
            <h3>7.1 Data We Collect</h3>
            <ul>
              <li>Personal information: name, email address, phone number</li>
              <li>Academic information: course enrollments, grades, attendance records</li>
              <li>Usage data: login activity, feature usage, session recordings</li>
              <li>Payment information: transaction details (processed securely via payment gateways)</li>
            </ul>
            <h3>7.2 How We Use Your Data</h3>
            <ul>
              <li>To provide and maintain the Service</li>
              <li>To personalize the learning experience</li>
              <li>To communicate updates, notifications, and reminders</li>
              <li>To process payments and manage fee records</li>
              <li>To improve the platform and develop new features</li>
            </ul>
          </section>

          <section className={SECTION}>
            <h2>8. Class Recordings &amp; Content</h2>
            <p>
              Live classes may be recorded for educational purposes. Participants will be informed
              when recording is active. Recorded content is made available to enrolled students and
              authorized users only. Unauthorized sharing or distribution of recorded content is
              strictly prohibited.
            </p>
          </section>

          <section className={SECTION}>
            <h2>9. AI-Powered Features</h2>
            <p>
              The Service includes AI-powered learning tools, including but not limited to AI
              practice sessions, mock interviews, and automated assessments. While we strive for
              accuracy, AI-generated content and evaluations should be used as supplementary learning
              tools and not as a sole measure of academic performance.
            </p>
          </section>

          <section className={SECTION}>
            <h2>10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, {company} shall not be liable
              for any indirect, incidental, special, consequential, or punitive damages, or any loss
              of profits or revenues, whether incurred directly or indirectly, or any loss of data,
              use, goodwill, or other intangible losses.
            </p>
          </section>

          <section className={SECTION}>
            <h2>11. Termination</h2>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without
              prior notice or liability, for any reason, including without limitation if you breach
              these Terms. Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section className={SECTION}>
            <h2>12. Changes to Terms</h2>
            <p>
              We reserve the right to modify or replace these Terms at any time. If a revision is
              material, we will provide at least 30 days' notice prior to any new terms taking
              effect. What constitutes a material change will be determined at our sole discretion.
              Your continued use of the Service after changes become effective constitutes acceptance
              of the revised Terms.
            </p>
          </section>

          <section className={SECTION}>
            <h2>13. Contact Us</h2>
            <p>
              If you have any questions about these Terms &amp; Conditions, please contact us at:
            </p>
            <div className={CONTACT_CARD}>
              <p><strong>{company}</strong></p>
              {email && <p>Email: {email}</p>}
              {isPlatformHost && <p>Website: www.lmscampus.com</p>}
            </div>
          </section>
          </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="tw-border-0 tw-border-t tw-border-solid tw-border-line tw-bg-surface-raised tw-px-6 tw-py-5">
        <div
          className={`${SHELL_INNER} tw-flex tw-items-center tw-justify-between max-md:tw-flex-col max-md:tw-gap-2.5 max-md:tw-text-center`}
        >
          {ready && (
            <>
              <img src={logoUrl || logo} alt={name} className="tw-h-6 tw-opacity-60" />
              <p className="tw-m-0 tw-text-[0.82rem] tw-text-content-subtle">
                &copy; {new Date().getFullYear()} {company}. All rights reserved.
              </p>
            </>
          )}
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
